import React, { useState, useEffect } from 'react';
import { categoryAPI } from '../services/categoryApi';
import { Category } from '../types';
import { useCompany } from '../context/CompanyContext';
import { useI18n } from '../hooks/useI18n';
import Layout from '../components/Layout';
import { Plus, Edit, Trash2, Search, AlertCircle, CheckCircle, X } from 'lucide-react';

const CategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);

  const { selectedCompany } = useCompany();
  const { t, isRTL } = useI18n();

  useEffect(() => {
    if (selectedCompany.selectedCompanyId) {
      loadCategories();
    } else {
      setCategories([]);
      setLoading(false);
    }
  }, [selectedCompany.selectedCompanyId]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await categoryAPI.getCategoriesByCompany(selectedCompany.selectedCompanyId!);
      setCategories(data);
      setError(null);
    } catch (err) {
      setError(t('stock.categories.failedToLoad'));
      console.error('Error loading categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async (categoryData: Partial<Category>) => {
    try {
      if (editingCategory) {
        const updated = await categoryAPI.updateCategory(editingCategory.id, categoryData);
        setCategories(categories.map(c => c.id === editingCategory.id ? updated : c));
        setSuccessMessage(t('stock.categories.categoryUpdated'));
      } else {
        const created = await categoryAPI.createCategory({
          ...categoryData,
          companyId: selectedCompany.selectedCompanyId!,
          active: true
        });
        setCategories([...categories, created]);
        setSuccessMessage(t('stock.categories.categoryCreated'));
      }
      setShowModal(false);
      setEditingCategory(null);
    } catch (err) {
      setError(t('stock.categories.failedToUpdate'));
      console.error('Error saving category:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('stock.categories.confirmDelete'))) {
      try {
        await categoryAPI.deleteCategory(id);
        setCategories(categories.filter(c => c.id !== id));
        setSuccessMessage(t('stock.categories.categoryDeleted'));
      } catch (err) {
        setError(t('stock.categories.failedToDelete'));
        console.error('Error deleting category:', err);
      }
    }
  };

  const filteredCategories = categories.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.description && c.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (!selectedCompany.selectedCompanyId) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('clients.noCompanySelected')}</h2>
            <p className="text-gray-600 mb-4">{t('clients.noCompanyMessage')}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`flex justify-between items-center mb-6 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('stock.categories.title')}</h1>
          <p className="text-gray-600 mt-1">
            {t('stock.categories.subtitle', { companyName: selectedCompany.selectedCompany?.name })}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setEditingCategory(null);
              setShowModal(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus size={20} />
            {t('stock.categories.createNew')}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)}><X size={18} /></button>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <CheckCircle size={18} />
            <span>{successMessage}</span>
          </div>
          <button onClick={() => setSuccessMessage(null)}><X size={18} /></button>
        </div>
      )}

      <div className="mb-6">
        <div className="relative">
          <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-400 z-10`} size={20} />
          <input
            type="text"
            placeholder={t('common.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
          />
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                {t('stock.categories.name')}
              </th>
              <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                {t('stock.categories.description')}
              </th>
              <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                {t('stock.categories.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredCategories.map((category) => (
              <tr key={category.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {category.name}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {category.description || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setEditingCategory(category);
                        setShowModal(true);
                      }}
                      className="text-green-600 hover:text-green-900"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(category.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredCategories.length === 0 && !loading && (
          <div className="text-center py-8 text-gray-500">
            {t('stock.categories.noCategoriesFound')}
          </div>
        )}
      </div>

      {showModal && (
        <CategoryModal
          category={editingCategory}
          onClose={() => setShowModal(false)}
          onSubmit={handleCreateOrUpdate}
        />
      )}
    </Layout>
  );
};

interface CategoryModalProps {
  category: Category | null;
  onClose: () => void;
  onSubmit: (data: Partial<Category>) => void;
}

const CategoryModal: React.FC<CategoryModalProps> = ({ category, onClose, onSubmit }) => {
  const { t, isRTL } = useI18n();
  const [name, setName] = useState(category?.name || '');
  const [description, setDescription] = useState(category?.description || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ name, description });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`bg-white rounded-lg p-6 w-full max-w-md ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <h2 className="text-2xl font-bold mb-4">
          {category ? t('stock.categories.editCategory') : t('stock.categories.createNew')}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('stock.categories.name')} *
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('stock.categories.description')}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>
          <div className={`flex justify-end gap-3 pt-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {category ? t('common.save') : t('common.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoriesPage;
